import React, { useMemo, useState } from 'react';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import { Filter, Layers, AlertTriangle, Clock, TrendingUp, TrendingDown, Activity, MapPin, Users, Shield, Target, Eye, Download, RefreshCw, Zap, BarChart3, X, Share2, FileText } from 'lucide-react';
import { priorityAlerts } from '../utils/dummyData';
import { locationReports, bindaRegions } from '../utils/mapData';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix for default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
// Custom marker icons
const createCustomIcon = (riskLevel: string) => {
  const colors = {
    critical: '#DC2626',
    high: '#F97316',
    moderate: '#EAB308',
    low: '#22C55E'
  };
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 20px;
        height: 20px;
        background-color: ${colors[riskLevel as keyof typeof colors]};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};
const StrategicDashboard = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('24h');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [selectedRisk, setSelectedRisk] = useState<string>('all');
  const [selectedDeputy, setSelectedDeputy] = useState<string>('all');
  const [selectedBinda, setSelectedBinda] = useState<string>('all');
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  // Filter reports based on selections
  const filteredReports = useMemo(() => {
    return locationReports.filter(report => {
      const categoryMatch = selectedCategory === 'all' || report.category === selectedCategory;
      const riskMatch = selectedRisk === 'all' || report.riskLevel === selectedRisk;
      const sourceMatch = selectedSource === 'all' || report.sources.includes(selectedSource);
      return categoryMatch && riskMatch && sourceMatch;
    });
  }, [selectedCategory, selectedRisk, selectedSource]);
  // Calculate statistics
  const stats = useMemo(() => {
    const critical = filteredReports.filter(r => r.riskLevel === 'critical').length;
    const high = filteredReports.filter(r => r.riskLevel === 'high').length;
    const moderate = filteredReports.filter(r => r.riskLevel === 'moderate').length;
    const low = filteredReports.filter(r => r.riskLevel === 'low').length;
    const ongoing = filteredReports.filter(r => r.status === 'ongoing').length;
    const monitoring = filteredReports.filter(r => r.status === 'monitoring').length;
    return {
      critical,
      high,
      moderate,
      low,
      ongoing,
      monitoring
    };
  }, [filteredReports]);
  // Category breakdown
  const categoryBreakdown = useMemo(() => {
    const categories = ['Ideologi', 'Politik', 'Ekonomi', 'Sosial Budaya', 'Pertahanan Keamanan'];
    return categories.map(cat => ({
      name: cat,
      count: locationReports.filter(r => r.category === cat).length
    }));
  }, []);
  // Regional breakdown
  const regionalBreakdown = useMemo(() => {
    const regions = bindaRegions.slice(0, 10);
    return regions.map(region => ({
      name: region,
      count: locationReports.filter(r => r.bindaRegion === region).length
    }));
  }, []);
  const categories = ['Ideologi', 'Politik', 'Ekonomi', 'Sosial Budaya', 'Pertahanan Keamanan'];
  const sources = ['OSINT', 'HUMINT', 'SIGINT', 'IMINT', 'GEOINT', 'FININT', 'TECHINT', 'SOCMINT'];
  const deputies = ['Deputi I', 'Deputi II', 'Deputi III', 'Deputi IV', 'Deputi V', 'Deputi VI', 'Deputi VII', 'Deputi VIII', 'Deputi IX'];
  const resetFilters = () => {
    setSelectedCategory('all');
    setSelectedTimeRange('24h');
    setSelectedSource('all');
    setSelectedRisk('all');
    setSelectedDeputy('all');
    setSelectedBinda('all');
  };
  const handleViewDetails = (alert: any) => {
    setSelectedAlert(alert);
    setShowDetailModal(true);
  };
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedAlert(null);
  };
  return <div className="space-y-6" data-id="element-1264">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1265">
        <div data-id="element-1266">
          <h1 className="text-2xl font-bold text-white" data-id="element-1267">
            Strategic Situational Dashboard
          </h1>
          <p className="text-sm text-gray-400 mt-1" data-id="element-1268">
            Real-time Intelligence Monitoring & Analysis
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-1269">
          <button className="flex items-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700" data-id="element-1270">
            <Download className="h-4 w-4 mr-2" data-id="element-1271" />
            Export Report
          </button>
          <button className="flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg" data-id="element-1272">
            <RefreshCw className="h-4 w-4 mr-2" data-id="element-1273" />
            Refresh
          </button>
          <div className="flex items-center px-3 py-2 bg-blue-900/30 rounded-lg text-xs text-blue-400 border border-blue-800/50" data-id="element-1274">
            <Clock className="h-3 w-3 mr-1 animate-pulse" data-id="element-1275" />
            <span data-id="element-1276">LIVE</span>
          </div>
        </div>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4" data-id="element-1277">
        <Card className="p-4" data-id="element-1278">
          <div className="flex items-center justify-between mb-2" data-id="element-1279">
            <span className="text-xs text-gray-400 uppercase" data-id="element-1280">
              Total Threats
            </span>
            <Activity className="h-4 w-4 text-cyan-400" data-id="element-1281" />
          </div>
          <div className="flex items-end justify-between" data-id="element-1282">
            <div data-id="element-1283">
              <p className="text-2xl font-bold text-white" data-id="element-1284">
                {filteredReports.length}
              </p>
              <p className="text-xs text-green-400 flex items-center mt-1" data-id="element-1285">
                <TrendingUp className="h-3 w-3 mr-1" data-id="element-1286" />
                +12% vs last week
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-red-800/30 bg-red-900/10" data-id="element-1287">
          <div className="flex items-center justify-between mb-2" data-id="element-1288">
            <span className="text-xs text-gray-400 uppercase" data-id="element-1289">Critical</span>
            <AlertTriangle className="h-4 w-4 text-red-400" data-id="element-1290" />
          </div>
          <div className="flex items-end justify-between" data-id="element-1291">
            <div data-id="element-1292">
              <p className="text-2xl font-bold text-red-400" data-id="element-1293">
                {stats.critical}
              </p>
              <p className="text-xs text-red-300 mt-1" data-id="element-1294">Immediate Action</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-orange-800/30 bg-orange-900/10" data-id="element-1295">
          <div className="flex items-center justify-between mb-2" data-id="element-1296">
            <span className="text-xs text-gray-400 uppercase" data-id="element-1297">High</span>
            <Shield className="h-4 w-4 text-orange-400" data-id="element-1298" />
          </div>
          <div className="flex items-end justify-between" data-id="element-1299">
            <div data-id="element-1300">
              <p className="text-2xl font-bold text-orange-400" data-id="element-1301">{stats.high}</p>
              <p className="text-xs text-orange-300 mt-1" data-id="element-1302">Priority Watch</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-yellow-800/30 bg-yellow-900/10" data-id="element-1303">
          <div className="flex items-center justify-between mb-2" data-id="element-1304">
            <span className="text-xs text-gray-400 uppercase" data-id="element-1305">Moderate</span>
            <Target className="h-4 w-4 text-yellow-400" data-id="element-1306" />
          </div>
          <div className="flex items-end justify-between" data-id="element-1307">
            <div data-id="element-1308">
              <p className="text-2xl font-bold text-yellow-400" data-id="element-1309">
                {stats.moderate}
              </p>
              <p className="text-xs text-yellow-300 mt-1" data-id="element-1310">Monitoring</p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-1311">
          <div className="flex items-center justify-between mb-2" data-id="element-1312">
            <span className="text-xs text-gray-400 uppercase" data-id="element-1313">
              Active Cases
            </span>
            <Zap className="h-4 w-4 text-cyan-400" data-id="element-1314" />
          </div>
          <div className="flex items-end justify-between" data-id="element-1315">
            <div data-id="element-1316">
              <p className="text-2xl font-bold text-white" data-id="element-1317">{stats.ongoing}</p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-1318">In Progress</p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-1319">
          <div className="flex items-center justify-between mb-2" data-id="element-1320">
            <span className="text-xs text-gray-400 uppercase" data-id="element-1321">
              BINDA Active
            </span>
            <Users className="h-4 w-4 text-blue-400" data-id="element-1322" />
          </div>
          <div className="flex items-end justify-between" data-id="element-1323">
            <div data-id="element-1324">
              <p className="text-2xl font-bold text-white" data-id="element-1325">
                {bindaRegions.length}
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-1326">All Regions</p>
            </div>
          </div>
        </Card>
      </div>
      {/* Standardized Comprehensive Filter Bar */}
      <Card className="p-4" data-id="element-1327">
        <div className="flex items-center justify-between mb-3" data-id="element-1328">
          <div className="flex items-center" data-id="element-1329">
            <Filter className="h-4 w-4 text-cyan-400 mr-2" data-id="element-1330" />
            <span className="text-sm font-semibold text-white uppercase" data-id="element-1331">
              Intelligence Filters
            </span>
          </div>
          <div className="flex items-center space-x-2" data-id="element-1332">
            <span className="text-xs text-gray-400" data-id="element-1333">
              {filteredReports.length} dari {locationReports.length} laporan
            </span>
            <button onClick={resetFilters} className="flex items-center px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs" data-id="element-1334">
              <X className="h-3 w-3 mr-1" data-id="element-1335" />
              Reset All
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3" data-id="element-1336">
          {/* Time Range Filter */}
          <div data-id="element-1337">
            <label className="block text-xs text-gray-400 mb-1" data-id="element-1338">
              Time Range
            </label>
            <select value={selectedTimeRange} onChange={e => setSelectedTimeRange(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-1339">
              <option value="1h" data-id="element-1340">Last 1 Hour</option>
              <option value="6h" data-id="element-1341">Last 6 Hours</option>
              <option value="24h" data-id="element-1342">Last 24 Hours</option>
              <option value="7d" data-id="element-1343">Last 7 Days</option>
              <option value="30d" data-id="element-1344">Last 30 Days</option>
            </select>
          </div>
          {/* Category Filter */}
          <div data-id="element-1345">
            <label className="block text-xs text-gray-400 mb-1" data-id="element-1346">Category</label>
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-1347">
              <option value="all" data-id="element-1348">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat} data-id="element-1349">
                  {cat}
                </option>)}
            </select>
          </div>
          {/* Risk Level Filter */}
          <div data-id="element-1350">
            <label className="block text-xs text-gray-400 mb-1" data-id="element-1351">
              Risk Level
            </label>
            <select value={selectedRisk} onChange={e => setSelectedRisk(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-1352">
              <option value="all" data-id="element-1353">All Levels</option>
              <option value="critical" data-id="element-1354">Critical</option>
              <option value="high" data-id="element-1355">High</option>
              <option value="moderate" data-id="element-1356">Moderate</option>
              <option value="low" data-id="element-1357">Low</option>
            </select>
          </div>
          {/* Deputy Filter */}
          <div data-id="element-1358">
            <label className="block text-xs text-gray-400 mb-1" data-id="element-1359">Deputy</label>
            <select value={selectedDeputy} onChange={e => setSelectedDeputy(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-1360">
              <option value="all" data-id="element-1361">All Deputies</option>
              {deputies.map(dep => <option key={dep} value={dep} data-id="element-1362">
                  {dep}
                </option>)}
            </select>
          </div>
          {/* BINDA Filter */}
          <div data-id="element-1363">
            <label className="block text-xs text-gray-400 mb-1" data-id="element-1364">
              BINDA Region
            </label>
            <select value={selectedBinda} onChange={e => setSelectedBinda(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-1365">
              <option value="all" data-id="element-1366">All BINDA</option>
              {bindaRegions.map(binda => <option key={binda} value={binda} data-id="element-1367">
                  {binda}
                </option>)}
            </select>
          </div>
          {/* Intel Source Filter */}
          <div data-id="element-1368">
            <label className="block text-xs text-gray-400 mb-1" data-id="element-1369">
              Intel Source
            </label>
            <select value={selectedSource} onChange={e => setSelectedSource(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-1370">
              <option value="all" data-id="element-1371">All Sources</option>
              {sources.map(source => <option key={source} value={source} data-id="element-1372">
                  {source}
                </option>)}
            </select>
          </div>
        </div>
      </Card>
      {/* Reorganized Layout - Map Full Width */}
      <Card className="overflow-hidden" data-id="element-1373">
        <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-gray-900 to-gray-800" data-id="element-1374">
          <div className="flex justify-between items-center" data-id="element-1375">
            <div data-id="element-1376">
              <h2 className="text-lg font-semibold text-white" data-id="element-1377">
                National Threat Map
              </h2>
              <p className="text-xs text-gray-400 mt-1" data-id="element-1378">
                {filteredReports.length} active incidents across Indonesia
              </p>
            </div>
            <div className="flex items-center space-x-2" data-id="element-1379">
              <Badge variant="green" data-id="element-1380">Live</Badge>
              <button className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg" data-id="element-1381">
                <Layers className="h-4 w-4 text-gray-400" data-id="element-1382" />
              </button>
              <button className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg" data-id="element-1383">
                <Eye className="h-4 w-4 text-gray-400" data-id="element-1384" />
              </button>
            </div>
          </div>
        </div>
        <div className="h-[500px] bg-[#0a1628]" data-id="element-1385">
          <MapContainer center={[-2.5489, 118.0149]} zoom={5} style={{
          height: '100%',
          width: '100%'
        }} className="z-0" data-id="element-1386">
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-1387" />
            {filteredReports.map(report => <Marker key={report.id} position={report.coordinates} icon={createCustomIcon(report.riskLevel)} data-id="element-1388">
                <Popup data-id="element-1389">
                  <div className="p-2 min-w-[200px]" data-id="element-1390">
                    <h3 className="font-semibold text-sm mb-1" data-id="element-1391">
                      {report.title}
                    </h3>
                    <div className="space-y-1 text-xs text-gray-600" data-id="element-1392">
                      <div className="flex items-center" data-id="element-1393">
                        <MapPin className="h-3 w-3 mr-1" data-id="element-1394" />
                        <span data-id="element-1395">{report.location}</span>
                      </div>
                      <div className="flex items-center" data-id="element-1396">
                        <span className="font-medium" data-id="element-1397">Category:</span>
                        <span className="ml-1" data-id="element-1398">{report.category}</span>
                      </div>
                    </div>
                    <div className="mt-2" data-id="element-1399">
                      <Badge variant={report.riskLevel === 'critical' ? 'red' : report.riskLevel === 'high' ? 'orange' : report.riskLevel === 'moderate' ? 'yellow' : 'green'} data-id="element-1400">
                        {report.riskLevel.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>
        <div className="p-3 border-t border-gray-800 bg-gray-900" data-id="element-1401">
          <div className="flex justify-between items-center" data-id="element-1402">
            <div className="flex space-x-4 text-xs" data-id="element-1403">
              <div className="flex items-center" data-id="element-1404">
                <div className="w-2 h-2 rounded-full bg-red-600 mr-1" data-id="element-1405"></div>
                <span className="text-gray-400" data-id="element-1406">
                  Critical ({stats.critical})
                </span>
              </div>
              <div className="flex items-center" data-id="element-1407">
                <div className="w-2 h-2 rounded-full bg-orange-500 mr-1" data-id="element-1408"></div>
                <span className="text-gray-400" data-id="element-1409">High ({stats.high})</span>
              </div>
              <div className="flex items-center" data-id="element-1410">
                <div className="w-2 h-2 rounded-full bg-yellow-500 mr-1" data-id="element-1411"></div>
                <span className="text-gray-400" data-id="element-1412">
                  Moderate ({stats.moderate})
                </span>
              </div>
              <div className="flex items-center" data-id="element-1413">
                <div className="w-2 h-2 rounded-full bg-green-500 mr-1" data-id="element-1414"></div>
                <span className="text-gray-400" data-id="element-1415">Low ({stats.low})</span>
              </div>
            </div>
            <span className="text-xs text-gray-400" data-id="element-1416">
              Updated: 2 minutes ago
            </span>
          </div>
        </div>
      </Card>
      {/* Priority Alerts - Now Below Map */}
      <Card className="flex flex-col" data-id="element-1417">
        <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-red-900/20 to-orange-900/20" data-id="element-1418">
          <div className="flex justify-between items-center" data-id="element-1419">
            <div data-id="element-1420">
              <h2 className="text-lg font-semibold text-white" data-id="element-1421">
                Priority Alerts
              </h2>
              <p className="text-xs text-gray-400 mt-1" data-id="element-1422">
                {priorityAlerts.length} high-priority incidents requiring
                immediate attention
              </p>
            </div>
            <AlertTriangle className="h-5 w-5 text-red-500 animate-pulse" data-id="element-1423" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4" data-id="element-1424">
          {priorityAlerts.map(alert => <div key={alert.id} className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg hover:border-cyan-500/50 transition-all cursor-pointer" data-id="element-1425">
              <div className="flex justify-between items-start mb-2" data-id="element-1426">
                <Badge variant={alert.riskLevel === 'critical' ? 'red' : alert.riskLevel === 'high' ? 'orange' : 'yellow'} data-id="element-1427">
                  {alert.riskLevel}
                </Badge>
                <span className="text-xs text-gray-400" data-id="element-1428">{alert.id}</span>
              </div>
              <h3 className="text-sm font-semibold text-white mb-2" data-id="element-1429">
                {alert.title}
              </h3>
              <div className="space-y-1 text-xs text-gray-400" data-id="element-1430">
                <div className="flex items-center" data-id="element-1431">
                  <MapPin className="h-3 w-3 mr-1" data-id="element-1432" />
                  <span data-id="element-1433">{alert.location}</span>
                </div>
                <div className="flex items-center" data-id="element-1434">
                  <Users className="h-3 w-3 mr-1" data-id="element-1435" />
                  <span data-id="element-1436">{alert.deputy}</span>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between" data-id="element-1437">
                <Badge variant={alert.status === 'ongoing' ? 'red' : alert.status === 'monitoring' ? 'yellow' : 'green'} data-id="element-1438">
                  {alert.status}
                </Badge>
                <button onClick={() => handleViewDetails(alert)} className="text-xs text-cyan-400 hover:text-cyan-300" data-id="element-1439">
                  View Details →
                </button>
              </div>
            </div>)}
        </div>
      </Card>
      {/* Detail Modal */}
      {showDetailModal && selectedAlert && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1440">
          <div className="bg-gradient-to-b from-gray-900 to-gray-800 border border-gray-700 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl" data-id="element-1441">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-red-900/20 to-orange-900/20 flex justify-between items-center sticky top-0 z-10" data-id="element-1442">
              <div className="flex items-center space-x-3" data-id="element-1443">
                <AlertTriangle className="h-6 w-6 text-red-500" data-id="element-1444" />
                <h3 className="text-xl font-bold text-white" data-id="element-1445">Alert Details</h3>
              </div>
              <button onClick={closeDetailModal} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" data-id="element-1446">
                <X className="h-5 w-5 text-gray-400 hover:text-white" data-id="element-1447" />
              </button>
            </div>
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]" data-id="element-1448">
              <div className="space-y-6" data-id="element-1449">
                {/* Title and Badges */}
                <div data-id="element-1450">
                  <h4 className="text-2xl font-bold text-white mb-3" data-id="element-1451">
                    {selectedAlert.title}
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-1452">
                    <Badge variant={selectedAlert.riskLevel === 'critical' ? 'red' : selectedAlert.riskLevel === 'high' ? 'orange' : 'yellow'} data-id="element-1453">
                      {selectedAlert.riskLevel.toUpperCase()}
                    </Badge>
                    <Badge variant="blue" data-id="element-1454">{selectedAlert.id}</Badge>
                    <Badge variant={selectedAlert.status === 'ongoing' ? 'red' : selectedAlert.status === 'monitoring' ? 'yellow' : 'green'} data-id="element-1455">
                      {selectedAlert.status}
                    </Badge>
                  </div>
                </div>
                {/* AI Summary */}
                <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-800/30 rounded-lg p-4" data-id="element-1456">
                  <div className="flex items-center mb-2" data-id="element-1457">
                    <div className="w-2 h-2 rounded-full bg-cyan-400 mr-2 animate-pulse" data-id="element-1458"></div>
                    <span className="text-xs font-semibold text-cyan-400 uppercase" data-id="element-1459">
                      AI Intelligence Summary
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-1460">
                    Intelligence analysis indicates a {selectedAlert.riskLevel}{' '}
                    level threat in {selectedAlert.location}. Immediate
                    monitoring and response protocols have been activated. The
                    situation requires coordinated action from{' '}
                    {selectedAlert.deputy} and local BINDA units. Current
                    status: {selectedAlert.status}.
                  </p>
                </div>
                {/* Key Information Grid */}
                <div className="grid grid-cols-2 gap-4" data-id="element-1461">
                  <div className="bg-gray-800/50 rounded-lg p-4" data-id="element-1462">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1463">Location</p>
                    <p className="text-sm text-white font-medium flex items-center" data-id="element-1464">
                      <MapPin className="h-4 w-4 mr-2 text-cyan-400" data-id="element-1465" />
                      {selectedAlert.location}
                    </p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4" data-id="element-1466">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1467">
                      Responsible Deputy
                    </p>
                    <p className="text-sm text-white font-medium flex items-center" data-id="element-1468">
                      <Users className="h-4 w-4 mr-2 text-blue-400" data-id="element-1469" />
                      {selectedAlert.deputy}
                    </p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4" data-id="element-1470">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1471">Alert ID</p>
                    <p className="text-sm text-white font-medium flex items-center" data-id="element-1472">
                      <FileText className="h-4 w-4 mr-2 text-purple-400" data-id="element-1473" />
                      {selectedAlert.id}
                    </p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4" data-id="element-1474">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1475">Status</p>
                    <Badge variant={selectedAlert.status === 'ongoing' ? 'red' : selectedAlert.status === 'monitoring' ? 'yellow' : 'green'} data-id="element-1476">
                      {selectedAlert.status}
                    </Badge>
                  </div>
                </div>
                {/* Detailed Description */}
                <div data-id="element-1477">
                  <h5 className="text-sm font-semibold text-white mb-3 flex items-center" data-id="element-1478">
                    <Shield className="h-4 w-4 mr-2 text-cyan-400" data-id="element-1479" />
                    Detailed Description
                  </h5>
                  <p className="text-sm text-gray-300 leading-relaxed bg-gray-800/30 p-4 rounded-lg" data-id="element-1480">
                    This alert has been flagged as {selectedAlert.riskLevel}{' '}
                    priority based on multiple intelligence sources and threat
                    assessment protocols. The situation in{' '}
                    {selectedAlert.location} requires immediate attention from{' '}
                    {selectedAlert.deputy}. Continuous monitoring is in place
                    with real-time updates being provided to all relevant
                    stakeholders. Coordination with local BINDA units has been
                    established for ground-level intelligence gathering and
                    response coordination.
                  </p>
                </div>
                {/* Threat Assessment */}
                <div className="bg-orange-900/20 border border-orange-800/30 rounded-lg p-4" data-id="element-1481">
                  <h5 className="text-sm font-semibold text-orange-400 mb-3 flex items-center" data-id="element-1482">
                    <Target className="h-4 w-4 mr-2" data-id="element-1483" />
                    Threat Assessment
                  </h5>
                  <div className="space-y-2 text-sm text-gray-300" data-id="element-1484">
                    <p data-id="element-1485">
                      • Risk Level:{' '}
                      <span className="text-white font-semibold" data-id="element-1486">
                        {selectedAlert.riskLevel.toUpperCase()}
                      </span>
                    </p>
                    <p data-id="element-1487">• Potential Impact: Regional security and stability</p>
                    <p data-id="element-1488">• Response Time Required: Immediate</p>
                    <p data-id="element-1489">• Coordination Level: Multi-agency</p>
                  </div>
                </div>
                {/* Recommended Actions */}
                <div data-id="element-1490">
                  <h5 className="text-sm font-semibold text-white mb-3 flex items-center" data-id="element-1491">
                    <Zap className="h-4 w-4 mr-2 text-yellow-400" data-id="element-1492" />
                    Recommended Actions
                  </h5>
                  <ul className="space-y-2" data-id="element-1493">
                    <li className="flex items-start" data-id="element-1494">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-cyan-600 text-xs text-white mr-3 mt-0.5 flex-shrink-0" data-id="element-1495">
                        1
                      </span>
                      <span className="text-sm text-gray-300" data-id="element-1496">
                        Activate immediate response protocols and alert all
                        relevant units
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1497">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-cyan-600 text-xs text-white mr-3 mt-0.5 flex-shrink-0" data-id="element-1498">
                        2
                      </span>
                      <span className="text-sm text-gray-300" data-id="element-1499">
                        Coordinate with {selectedAlert.deputy} for strategic
                        oversight
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1500">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-cyan-600 text-xs text-white mr-3 mt-0.5 flex-shrink-0" data-id="element-1501">
                        3
                      </span>
                      <span className="text-sm text-gray-300" data-id="element-1502">
                        Deploy additional intelligence assets to{' '}
                        {selectedAlert.location}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1503">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-cyan-600 text-xs text-white mr-3 mt-0.5 flex-shrink-0" data-id="element-1504">
                        4
                      </span>
                      <span className="text-sm text-gray-300" data-id="element-1505">
                        Establish continuous monitoring and reporting protocols
                      </span>
                    </li>
                  </ul>
                </div>
                {/* Intelligence Sources */}
                <div data-id="element-1506">
                  <h5 className="text-sm font-semibold text-white mb-3 flex items-center" data-id="element-1507">
                    <Activity className="h-4 w-4 mr-2 text-green-400" data-id="element-1508" />
                    Intelligence Sources
                  </h5>
                  <div className="flex flex-wrap gap-2" data-id="element-1509">
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-700" data-id="element-1510">
                      OSINT
                    </span>
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-700" data-id="element-1511">
                      HUMINT
                    </span>
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-700" data-id="element-1512">
                      SIGINT
                    </span>
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-700" data-id="element-1513">
                      GEOINT
                    </span>
                  </div>
                </div>
                {/* Timeline */}
                <div data-id="element-1514">
                  <h5 className="text-sm font-semibold text-white mb-3 flex items-center" data-id="element-1515">
                    <Clock className="h-4 w-4 mr-2 text-blue-400" data-id="element-1516" />
                    Activity Timeline
                  </h5>
                  <div className="space-y-3" data-id="element-1517">
                    <div className="flex items-start" data-id="element-1518">
                      <div className="w-2 h-2 rounded-full bg-cyan-400 mr-3 mt-1.5" data-id="element-1519"></div>
                      <div data-id="element-1520">
                        <p className="text-sm text-white" data-id="element-1521">Alert Created</p>
                        <p className="text-xs text-gray-400" data-id="element-1522">2 hours ago</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1523">
                      <div className="w-2 h-2 rounded-full bg-yellow-400 mr-3 mt-1.5" data-id="element-1524"></div>
                      <div data-id="element-1525">
                        <p className="text-sm text-white" data-id="element-1526">
                          Risk Assessment Completed
                        </p>
                        <p className="text-xs text-gray-400" data-id="element-1527">1 hour ago</p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1528">
                      <div className="w-2 h-2 rounded-full bg-green-400 mr-3 mt-1.5 animate-pulse" data-id="element-1529"></div>
                      <div data-id="element-1530">
                        <p className="text-sm text-white" data-id="element-1531">Active Monitoring</p>
                        <p className="text-xs text-gray-400" data-id="element-1532">Ongoing</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Action Buttons */}
                <div className="flex flex-col space-y-3 pt-4 border-t border-gray-700" data-id="element-1533">
                  <button className="w-full flex items-center justify-center px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium" data-id="element-1534">
                    <Eye className="h-4 w-4 mr-2" data-id="element-1535" />
                    Open Full Case File
                  </button>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1536">
                    <button className="flex items-center justify-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg" data-id="element-1537">
                      <Download className="h-4 w-4 mr-2" data-id="element-1538" />
                      Export Report
                    </button>
                    <button className="flex items-center justify-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg" data-id="element-1539">
                      <Share2 className="h-4 w-4 mr-2" data-id="element-1540" />
                      Share Alert
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Analysis Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1541">
        {/* Category Breakdown */}
        <Card className="p-4" data-id="element-1542">
          <div className="flex justify-between items-center mb-4" data-id="element-1543">
            <h3 className="text-lg font-semibold text-white" data-id="element-1544">
              Category Breakdown
            </h3>
            <BarChart3 className="h-5 w-5 text-cyan-400" data-id="element-1545" />
          </div>
          <div className="space-y-3" data-id="element-1546">
            {categoryBreakdown.map(cat => <div key={cat.name} data-id="element-1547">
                <div className="flex justify-between items-center mb-1" data-id="element-1548">
                  <span className="text-sm text-gray-300" data-id="element-1549">{cat.name}</span>
                  <span className="text-sm font-semibold text-white" data-id="element-1550">
                    {cat.count}
                  </span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden" data-id="element-1551">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{
                width: `${cat.count / locationReports.length * 100}%`
              }} data-id="element-1552"></div>
                </div>
              </div>)}
          </div>
        </Card>
        {/* Regional Analysis */}
        <Card className="p-4" data-id="element-1553">
          <div className="flex justify-between items-center mb-4" data-id="element-1554">
            <h3 className="text-lg font-semibold text-white" data-id="element-1555">
              Top 10 BINDA Regions
            </h3>
            <MapPin className="h-5 w-5 text-orange-400" data-id="element-1556" />
          </div>
          <div className="space-y-2" data-id="element-1557">
            {regionalBreakdown.map((region, index) => <div key={region.name} className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg" data-id="element-1558">
                <div className="flex items-center" data-id="element-1559">
                  <span className="w-6 h-6 rounded-full bg-cyan-600 text-white text-xs flex items-center justify-center mr-2" data-id="element-1560">
                    {index + 1}
                  </span>
                  <span className="text-sm text-gray-300" data-id="element-1561">{region.name}</span>
                </div>
                <span className="text-sm font-semibold text-white" data-id="element-1562">
                  {region.count}
                </span>
              </div>)}
          </div>
        </Card>
        {/* Status Overview */}
        <Card className="p-4" data-id="element-1563">
          <div className="flex justify-between items-center mb-4" data-id="element-1564">
            <h3 className="text-lg font-semibold text-white" data-id="element-1565">
              Status Overview
            </h3>
            <Activity className="h-5 w-5 text-green-400" data-id="element-1566" />
          </div>
          <div className="space-y-4" data-id="element-1567">
            <div className="p-4 bg-red-900/20 border border-red-800/30 rounded-lg" data-id="element-1568">
              <div className="flex justify-between items-center mb-2" data-id="element-1569">
                <span className="text-sm text-red-400" data-id="element-1570">Ongoing Operations</span>
                <span className="text-2xl font-bold text-red-400" data-id="element-1571">
                  {stats.ongoing}
                </span>
              </div>
              <p className="text-xs text-gray-400" data-id="element-1572">
                Active response in progress
              </p>
            </div>
            <div className="p-4 bg-yellow-900/20 border border-yellow-800/30 rounded-lg" data-id="element-1573">
              <div className="flex justify-between items-center mb-2" data-id="element-1574">
                <span className="text-sm text-yellow-400" data-id="element-1575">
                  Under Monitoring
                </span>
                <span className="text-2xl font-bold text-yellow-400" data-id="element-1576">
                  {stats.monitoring}
                </span>
              </div>
              <p className="text-xs text-gray-400" data-id="element-1577">Continuous surveillance</p>
            </div>
            <div className="p-4 bg-green-900/20 border border-green-800/30 rounded-lg" data-id="element-1578">
              <div className="flex justify-between items-center mb-2" data-id="element-1579">
                <span className="text-sm text-green-400" data-id="element-1580">Resolved Today</span>
                <span className="text-2xl font-bold text-green-400" data-id="element-1581">7</span>
              </div>
              <p className="text-xs text-gray-400" data-id="element-1582">Successfully mitigated</p>
            </div>
          </div>
        </Card>
      </div>
      {/* Timeline & Trends */}
      <Card className="p-4" data-id="element-1583">
        <div className="flex justify-between items-center mb-4" data-id="element-1584">
          <div data-id="element-1585">
            <h2 className="text-lg font-semibold text-white" data-id="element-1586">
              Intelligence Timeline & Trends
            </h2>
            <p className="text-xs text-gray-400 mt-1" data-id="element-1587">
              30-day incident pattern analysis
            </p>
          </div>
          <select className="bg-gray-800 text-sm text-white border border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500" data-id="element-1588">
            <option data-id="element-1589">Last 30 Days</option>
            <option data-id="element-1590">Last 7 Days</option>
            <option data-id="element-1591">Last 90 Days</option>
          </select>
        </div>
        <div className="h-[250px] bg-gradient-to-b from-gray-900 to-[#0a1628] rounded-lg p-4 flex items-end justify-between relative" data-id="element-1592">
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none p-4" data-id="element-1593">
            <div className="w-full h-px bg-gray-800" data-id="element-1594"></div>
            <div className="w-full h-px bg-gray-800" data-id="element-1595"></div>
            <div className="w-full h-px bg-gray-800" data-id="element-1596"></div>
            <div className="w-full h-px bg-gray-800" data-id="element-1597"></div>
          </div>
          <div className="flex items-end space-x-2 w-full h-full relative z-10" data-id="element-1598">
            {[35, 45, 30, 60, 50, 75, 65, 55, 70, 85, 75, 90, 65, 60, 80, 70, 75, 85, 90, 80, 70, 65, 75, 80, 85, 90, 85, 80, 75, 70].map((value, index) => <div key={index} className="bg-gradient-to-t from-cyan-600 to-cyan-400 w-full rounded-t hover:from-cyan-500 hover:to-cyan-300 transition-all cursor-pointer" style={{
            height: `${value}%`
          }} title={`Day ${index + 1}: ${value} incidents`} data-id="element-1599"></div>)}
          </div>
        </div>
        <div className="flex justify-between mt-3 text-xs text-gray-400" data-id="element-1600">
          <span data-id="element-1601">1 Aug</span>
          <span data-id="element-1602">15 Aug</span>
          <span data-id="element-1603">31 Aug</span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4" data-id="element-1604">
          <div className="text-center p-3 bg-gray-800/50 rounded-lg" data-id="element-1605">
            <p className="text-xs text-gray-400" data-id="element-1606">Average Daily</p>
            <p className="text-xl font-bold text-white mt-1" data-id="element-1607">23</p>
            <p className="text-xs text-green-400 mt-1" data-id="element-1608">+15% vs last month</p>
          </div>
          <div className="text-center p-3 bg-gray-800/50 rounded-lg" data-id="element-1609">
            <p className="text-xs text-gray-400" data-id="element-1610">Peak Day</p>
            <p className="text-xl font-bold text-white mt-1" data-id="element-1611">27 Aug</p>
            <p className="text-xs text-orange-400 mt-1" data-id="element-1612">45 incidents</p>
          </div>
          <div className="text-center p-3 bg-gray-800/50 rounded-lg" data-id="element-1613">
            <p className="text-xs text-gray-400" data-id="element-1614">Response Time</p>
            <p className="text-xl font-bold text-white mt-1" data-id="element-1615">2.4h</p>
            <p className="text-xs text-green-400 mt-1" data-id="element-1616">-20% improvement</p>
          </div>
        </div>
      </Card>
    </div>;
};
export default StrategicDashboard;